import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Note } from "@pagemates/shared";
import { api, describeError } from "../../lib/api";
import { Spinner } from "../../components/ui";
import { CloseIcon, MoreIcon, NoteIcon, QuoteIcon, SendIcon } from "../../components/Icons";
import { posLabel, type PosCtx } from "../../lib/format";
import { BackBar } from "../../components/ui";
import { useClub } from "./ClubLayout";
import { PaperNote, spot } from "../../components/Moments";
import type { ForwardNote } from "@pagemates/shared";
import { FORWARD_NOTE_MAX_BODY } from "@pagemates/shared";
import { useClubBooks, useMe } from "../../lib/queries";
import { notes } from "@pagemates/shared/copy";

const txt = notes.page;

const when = (iso: string) => {
  const d = new Date(iso), now = new Date();
  if (d.toDateString() === now.toDateString()) return txt.today;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", ...(d.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }) });
};

// Your private notebook for one book. Nobody else can open it.
export function NotesPage() {
  const { clubId } = useClub();
  const { bookId } = useParams({ from: "/authed/clubs/$clubId/notes/$bookId" });
  const qc = useQueryClient();
  const books = useClubBooks(clubId);
  const notes = useQuery({ queryKey: ["notes", clubId, bookId], queryFn: () => api.notes(clubId, bookId) });
  const me = useMe();
  const book = books.data ? [books.data.current, ...books.data.onDeck, ...books.data.finished].find((b) => b && b.id === bookId) : undefined;
  const myProgress = books.data?.current?.id === bookId ? books.data.current.progress.find((p) => p.id === me.data?.id)?.percent : undefined;
  const ctx: PosCtx = { pages: book?.book.pages ?? null, audioSeconds: book?.book.audioSeconds ?? null, myFormat: book?.myFormat ?? "pages", landmarks: book?.plan?.landmarks ?? [] };
  const fwd = useQuery({ queryKey: ["forward-notes", clubId, bookId], queryFn: () => api.forwardNotes(clubId, bookId) });
  const received = fwd.data?.received ?? [], left = fwd.data?.mine ?? [];
  const listRef = useRef<HTMLDivElement>(null);
  // Newest note in view. On phones the list scrolls inside itself, so the back bar and title stay put.
  useEffect(() => {
    const l = listRef.current;
    if (l && l.scrollHeight > l.clientHeight) l.scrollTop = l.scrollHeight;
    else l?.lastElementChild?.scrollIntoView({ block: "end" });
  }, [notes.data?.length, fwd.data]);
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ["notes", clubId, bookId] }), qc.invalidateQueries({ queryKey: ["books", clubId] })]);

  if (notes.isPending || books.isPending) return <main className="page"><Spinner /></main>;
  if (notes.isError) return <main className="page"><p className="error">{describeError(notes.error)}</p></main>;

  return (
    <main className="page notebook">
      <div className="nb-head">
        <BackBar label={txt.back} onClick={() => history.back()} />
        <div className="eyebrow">{txt.notebookOnlyYouCan}</div>
        <h1 className="book">{book?.book.title ?? txt.notes}</h1>
      </div>
      <div className="nb-list" ref={listRef}>
        {received.length > 0 && <div className="nb-sect">{txt.leftForYou}</div>}
        {received.map((n) => <PaperNote key={n.id} n={n} ctx={ctx} clubId={clubId} bookId={bookId} />)}
        {(received.length > 0 || left.length > 0) && <div className="nb-sect">{txt.yourNotes}</div>}
        {notes.data.length === 0 && <p className="muted small">{txt.nothingYetJotAs}</p>}
        {notes.data.map((n) => <NoteItem key={n.id} n={n} clubId={clubId} bookId={bookId} ctx={ctx} onChange={refresh} />)}
        {left.length > 0 && <div className="nb-sect">{txt.notesYouVeLeft}</div>}
        {left.map((n) => <LeftNote key={n.id} n={n} ctx={ctx} clubId={clubId} bookId={bookId} />)}
      </div>
      <NoteComposer clubId={clubId} bookId={bookId} defaultPosition={myProgress ?? 0} ctx={ctx} onSaved={refresh} />
    </main>
  );
}

function NoteItem({ n, clubId, bookId, ctx, onChange }: { n: Note; clubId: string; bookId: string; ctx: PosCtx; onChange: () => Promise<unknown> }) {
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(n.body);
  const [confirm, setConfirm] = useState<"delete" | "share" | "shareflag" | null>(null);
  const [up, setUp] = useState(false); // open the menu upward only when there's no room below
  const ref = useRef<HTMLDivElement>(null);
  const edit = useMutation({ mutationFn: () => api.editNote(clubId, bookId, n.id, { body }), onSuccess: async () => { await onChange(); setEditing(false); } });
  const del = useMutation({ mutationFn: () => api.deleteNote(clubId, bookId, n.id), onSuccess: onChange });
  const share = useMutation({ mutationFn: (flag: boolean) => api.shareNote(clubId, bookId, n.id, flag), onSuccess: async () => { await onChange(); setConfirm(null); } });
  const unshare = useMutation({ mutationFn: () => api.unshareNote(clubId, bookId, n.id), onSuccess: onChange });
  useEffect(() => {
    if (!menu) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setMenu(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menu]);

  return (
    <div className={`note${n.kind === "quote" ? " is-quote" : ""}`} ref={ref}>
      {editing ? (
        <form className="stack-sm" onSubmit={(e) => { e.preventDefault(); edit.mutate(); }}>
          <textarea className="input" value={body} maxLength={4000} onChange={(e) => setBody(e.target.value)} autoFocus />
          <div className="row"><button type="submit" className="btn primary sm" disabled={!body.trim() || edit.isPending}>{txt.save}</button><button type="button" className="btn ghost sm" onClick={() => { setEditing(false); setBody(n.body); }}>{txt.cancel}</button></div>
          {edit.isError && <p className="error">{describeError(edit.error)}</p>}
        </form>
      ) : n.kind === "quote" ? (
        <blockquote className="qblock"><span className="qmark" aria-hidden="true">“</span>{n.body}</blockquote>
      ) : (
        <div className="note-body">{n.body}</div>
      )}
      <div className="note-meta">
        <span className="pos mine">{n.position === 0 ? txt.open : posLabel(n.position, ctx)}</span>
        <span>{when(n.createdAt)}{n.editedAt ? txt.edited : ""}</span>
        {n.sharedCommentId && <Link to="/clubs/$clubId/talk" params={{ clubId }} search={{ book: bookId, thread: n.sharedCommentId }} className="shared">{txt.shared(n.kind === "quote" ? txt.asAQuote : "")}</Link>}
        <button type="button" className="more" aria-label={txt.noteActions} aria-expanded={menu} onClick={() => { setUp((ref.current?.getBoundingClientRect().bottom ?? 0) > window.innerHeight - 240); setMenu((m) => !m); }}><MoreIcon width={18} height={18} /></button>
        {menu && (
          <div className={`menu${up ? " up" : ""}`} role="menu">
            {!n.sharedCommentId && <button role="menuitem" onClick={() => { setMenu(false); setConfirm("share"); }}>{txt.shareWithTheClub}</button>}
            {!n.sharedCommentId && <button role="menuitem" onClick={() => { setMenu(false); setConfirm("shareflag"); }}>{txt.shareAndFlagFor}</button>}
            {n.sharedCommentId && <button role="menuitem" disabled={unshare.isPending} onClick={() => { setMenu(false); unshare.mutate(); }}>{txt.takeItBackFrom}</button>}
            <button role="menuitem" onClick={() => { setMenu(false); setEditing(true); }}>{txt.edit}</button>
            <button role="menuitem" className="danger" onClick={() => { setMenu(false); setConfirm("delete"); }}>{txt.delete}</button>
          </div>
        )}
      </div>
      {confirm && (
        <div className="row small" style={{ marginTop: 6, flexWrap: "wrap", gap: 8 }}>
          <span className="muted">
            {confirm === "delete" ? txt.deleteThisNote : txt.postItToTalk(n.position === 0 ? "spoiler-free" : `${n.position}%`, n.kind === "quote" ? txt.asAQuote : "", confirm === "shareflag" ? txt.flaggedForTheMeeting : "")}
          </span>
          <button className={`btn sm ${confirm === "delete" ? "danger" : "primary"}`} disabled={del.isPending || share.isPending}
            onClick={() => confirm === "delete" ? del.mutate() : share.mutate(confirm === "shareflag")}>{confirm === "delete" ? txt.delete : txt.share}</button>
          <button className="btn ghost sm" onClick={() => setConfirm(null)}>{txt.cancel}</button>
          {(del.isError || share.isError) && <p className="error">{describeError(del.error ?? share.error)}</p>}
          {unshare.isError && <p className="error small">{describeError(unshare.error) === "has_replies" ? txt.someoneHasRepliedSo : describeError(unshare.error)}</p>}
        </div>
      )}
      {share.isSuccess && share.data && <p className="small"><Link to="/clubs/$clubId/talk" params={{ clubId }} search={{ book: bookId, thread: share.data.commentId }}>{txt.openItInTalk}</Link></p>}
    </div>
  );
}

export function NoteComposer({ clubId, bookId, defaultPosition, ctx, onSaved, compact }:
  { clubId: string; bookId: string; defaultPosition: number; ctx?: PosCtx; onSaved: () => Promise<unknown>; compact?: boolean }) {
  const [body, setBody] = useState("");
  const [quote, setQuote] = useState(false);
  const [position, setPosition] = useState<number | null>(null);
  const [pick, setPick] = useState(false);
  const ta = useRef<HTMLTextAreaElement>(null);
  const pickRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!pick) return;
    const close = (e: MouseEvent) => { if (!pickRef.current?.contains(e.target as Node)) setPick(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [pick]);
  const save = useMutation({
    mutationFn: () => api.addNote(clubId, bookId, { body, kind: quote ? "quote" : "note", position: position ?? defaultPosition }),
    onSuccess: async () => { setBody(""); setQuote(false); setPosition(null); if (ta.current) ta.current.style.height = "auto"; await onSaved(); },
  });
  function grow(el: HTMLTextAreaElement) { el.style.height = "auto"; el.style.height = `${Math.min(el.scrollHeight, 7.2 * 16)}px`; }
  const pos = position ?? defaultPosition;
  const chipText = ctx ? posLabel(pos, ctx) : `${pos}%`;
  return (
    <form className={`composer nb-composer${compact ? " compact" : ""}`} onSubmit={(e) => { e.preventDefault(); if (body.trim()) save.mutate(); }}>
      {quote && (
        <div className="mode-strip">
          <QuoteIcon /><div className="t"><b>{txt.aPassageFromThe}</b><small>{txt.keptInTheBook}</small></div>
          <button type="button" className="x" aria-label={txt.backToAPlain} onClick={() => setQuote(false)}><CloseIcon /></button>
        </div>
      )}
      <div className="pill-row">
        <button type="button" className={`plus${quote ? " on" : ""}`} aria-pressed={quote} aria-label={txt.saveAsAPassage} title={txt.aPassageFromThe} onClick={() => { setQuote((q) => !q); ta.current?.focus(); }}><QuoteIcon /></button>
        <div className={`pill-box${quote ? " quoting" : ""}`}>
          <textarea ref={ta} rows={1} value={body} placeholder={quote ? txt.pasteThePassage : txt.noteToSelf} maxLength={4000}
            onChange={(e) => { setBody(e.target.value); grow(e.target); }}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && window.matchMedia("(pointer: fine)").matches) { e.preventDefault(); if (body.trim() && !save.isPending) save.mutate(); } }} />
          <div className="pill-tail" ref={pickRef}>
            <button type="button" className="chip" aria-label={txt.whereInTheBook(chipText)} aria-expanded={pick} onClick={() => setPick((p) => !p)}>{chipText}</button>
            <button type="submit" className="send" disabled={!body.trim() || save.isPending} aria-label={txt.saveNote}><SendIcon /></button>
            {pick && (
              <div className="popover nb-pick" role="group" aria-label={txt.whereInTheBook2}>
                <button type="button" role="radio" aria-checked={position === null} onClick={() => { setPosition(null); setPick(false); }}><b>{txt.whereIAm(defaultPosition)}</b><span>{txt.theNoteSitsAt}</span></button>
                <div className="custom">
                  <span><b>{txt.somewhereElse}</b><span>{txt.percentThroughTheBook}</span></span>
                  <input className="input num" type="number" min={0} max={100} value={pos} aria-label={txt.percent}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setPick(false); } }}
                    onChange={(e) => setPosition(Math.max(0, Math.min(100, Number(e.target.value) || 0)))} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {save.isError && <p className="error hint-line">{describeError(save.error)}</p>}
    </form>
  );
}

/**
 * A note you left forward. To you it is always waiting at its page: nothing here (or anywhere) says whether it
 * has reached anyone. Editing reaches copies that already arrived; deleting takes it back from everyone.
 */
function LeftNote({ n, ctx, clubId, bookId }: { n: ForwardNote; ctx: PosCtx; clubId: string; bookId: string }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [body, setBody] = useState(n.body);
  const refresh = () => qc.invalidateQueries({ queryKey: ["forward-notes", clubId, bookId] });
  const edit = useMutation({ mutationFn: () => api.editForwardNote(clubId, bookId, n.id, { body }), onSuccess: async () => { await refresh(); setEditing(false); } });
  const del = useMutation({ mutationFn: () => api.deleteForwardNote(clubId, bookId, n.id), onSuccess: refresh });
  return (
    <div className="note left-note">
      <span className="waiting">{txt.waitingAt(spot(n.position, ctx), n.recipient ? txt.for(n.recipient.name) : txt.forEveryone)}</span>
      {editing ? (
        <form className="stack-sm" onSubmit={(e) => { e.preventDefault(); if (body.trim()) edit.mutate(); }}>
          <textarea className="input book" value={body} maxLength={FORWARD_NOTE_MAX_BODY} onChange={(e) => setBody(e.target.value)} autoFocus aria-label={txt.yourNote} />
          <div className="row"><button type="submit" className="btn primary sm" disabled={!body.trim() || edit.isPending}>{txt.save}</button><button type="button" className="btn ghost sm" onClick={() => { setEditing(false); setBody(n.body); }}>{txt.cancel}</button></div>
        </form>
      ) : <p className="book" style={{ margin: 0 }}>{n.body}</p>}
      {!editing && !confirm && <div className="small"><button type="button" className="linkish" onClick={() => setEditing(true)}>{txt.edit}</button> · <button type="button" className="linkish danger-text" onClick={() => setConfirm(true)}>{txt.delete}</button></div>}
      {confirm && (
        <div className="row small"><span className="muted">{txt.deleteItItS}</span>
          <button type="button" className="btn danger sm" disabled={del.isPending} onClick={() => del.mutate()}>{txt.delete}</button>
          <button type="button" className="btn ghost sm" onClick={() => setConfirm(false)}>{txt.keep}</button></div>
      )}
      {(edit.isError || del.isError) && <p className="error small">{describeError(edit.error ?? del.error)}</p>}
    </div>
  );
}
